import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useGet from '../Hooks/useGet';
import usePost from '../Hooks/usePost';
import API_ENDPOINTS from '../Config/api';
import ClipLoader from 'react-spinners/ClipLoader';
import { Icon } from '../Components/UI/Icon';
import { toast } from 'react-toastify';
import HeaderBanner from '../Components/HeaderBanner';

export default function QuestionarioReview() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [answers, setAnswers] = useState({});
  const [initialAnswers, setInitialAnswers] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [questionTypes, setQuestionTypes] = useState({});
  const [filterMode, setFilterMode] = useState('editable'); // 'all' or 'editable'

  // Recupera tutte le risposte del questionario
  const { fetchedData: risposteData } = useGet(
    API_ENDPOINTS.questionarioRisposte(id)
  );
  
  const { postData } = usePost();

  // Ottieni l'elenco delle sezioni nell'ordine corretto
  const getSectionsList = () => {
    if (!risposteData?.sezioni) return [];
    return Object.keys(risposteData.sezioni);
  };

  // Funzioni per navigare tra le sezioni
  const goToNextSection = () => {
    const sections = getSectionsList();
    const currentIndex = sections.indexOf(selectedSection);
    if (currentIndex < sections.length - 1) {
      setSelectedSection(sections[currentIndex + 1]);
    }
  };

  const goToPreviousSection = () => {
    const sections = getSectionsList();
    const currentIndex = sections.indexOf(selectedSection);
    if (currentIndex > 0) {
      setSelectedSection(sections[currentIndex - 1]);
    }
  };

  const isLastSection = () => {
    const sections = getSectionsList();
    return selectedSection === sections[sections.length - 1];
  };

  const isFirstSection = () => {
    const sections = getSectionsList();
    return selectedSection === sections[0];
  };

  useEffect(() => {
    if (risposteData?.sezioni && typeof risposteData.sezioni === 'object') {
      const computedInitialAnswers = {};
      const typesMap = {};
      const sezioniArray = Object.values(risposteData.sezioni);
      
      sezioniArray.forEach((sezione) => {
        if (sezione && sezione.risposte && Array.isArray(sezione.risposte)) {
          sezione.risposte.forEach((risposta) => {
            if (!risposta) return;
            if (risposta.domanda_id && risposta.domanda_tipo) {
              typesMap[risposta.domanda_id] = risposta.domanda_tipo;
            }
            
            if (risposta.opzione_selezionata) {
              computedInitialAnswers[risposta.domanda_id] = [risposta.opzione_selezionata.id];
            } else if (risposta.opzioni_selezionate && Array.isArray(risposta.opzioni_selezionate) && risposta.opzioni_selezionate.length > 0) {
              computedInitialAnswers[risposta.domanda_id] = risposta.opzioni_selezionate.map(o => o.id);
            } else if (risposta.testo_risposta) {
              computedInitialAnswers[risposta.domanda_id] = risposta.testo_risposta;
            }
          });
        }
      });

      // Set question types always
      setQuestionTypes(typesMap);

      // Initialize answers only once (avoid overwriting user edits when switching sections)
      setAnswers((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        return computedInitialAnswers;
      });
      setInitialAnswers((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        return computedInitialAnswers;
      });
      
      if (!selectedSection && risposteData.sezioni) {
        const sectionKeys = Object.keys(risposteData.sezioni);
        if (sectionKeys.length > 0) {
          setSelectedSection(sectionKeys[0]);
        }
      }
    }
  }, [risposteData]);

  const handleOptionChange = (questionId, optionId, isMultiple) => {
    setAnswers((prevAnswers) => {
      const prevOptions = prevAnswers[questionId] || [];
      let newAnswer;
      if (isMultiple) {
        newAnswer = prevOptions.includes(optionId)
          ? prevOptions.filter((id) => id !== optionId)
          : [...prevOptions, optionId];
      } else {
        newAnswer = [optionId];
      }
      setHasChanges(true);
      return {
        ...prevAnswers,
        [questionId]: newAnswer,
      };
    });
  };

  const handleTextChange = (questionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: text,
    }));
    setHasChanges(true);
  };

  // Funzione per determinare se una domanda è modificabile (risposta NO o NON SO)
  const isQuestionEditable = (domanda) => {
    if (!domanda || !domanda.opzione_selezionata) return false;
    const testoRisposta = domanda.opzione_selezionata.testo?.toUpperCase();
    return testoRisposta === 'NO' || testoRisposta === 'NON SO';
  };

  // Filtra le domande in base alla modalità
  const filterQuestions = (risposte) => {
    if (!risposte || !Array.isArray(risposte)) return [];
    if (filterMode === 'all') return risposte;
    return risposte.filter(domanda => isQuestionEditable(domanda));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only save answers that have changed
      const changedAnswers = Object.entries(answers).filter(([domandaId, answer]) => {
        const initial = initialAnswers[domandaId];
        if (!initial) return true; // New answer
        
        // Compare arrays
        if (Array.isArray(answer) && Array.isArray(initial)) {
          if (answer.length !== initial.length) return true;
          return !answer.every((val, idx) => val === initial[idx]);
        }
        
        // Compare strings
        return answer !== initial;
      });

      if (changedAnswers.length === 0) {
        toast.info(t('questionario.noChanges', 'Nessuna modifica da salvare'));
        setSaving(false);
        return;
      }

      const savePromises = changedAnswers.map(([domandaId, answer]) => {
        const tipo = questionTypes[domandaId];
        const payload = { domanda_id: domandaId };

        if ((tipo === 'libera' || tipo === 'testo') && typeof answer === 'string') {
          payload.testo_risposta = answer;
        } else if (Array.isArray(answer)) {
          // Backend expects single option id even for 'multipla'
          payload.opzione_id = answer[0] || '';
        }

        return postData(API_ENDPOINTS.saveRisposta(id), payload);
      });

      await Promise.all(savePromises);
      
      toast.success(t('questionario.saveSuccess', `${changedAnswers.length} risposte salvate con successo`));
      setHasChanges(false);
      setInitialAnswers({...answers}); // Update initial answers after successful save
      
      setTimeout(() => {
        navigate('/compliance');
      }, 1500);
    } catch (error) {
      toast.error(t('questionario.saveError', 'Errore nel salvataggio delle risposte'));
      console.error('Error saving answers:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!risposteData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderBanner title={t('Compliance.title')} linkTo='/compliance' description={t('questionario.reviewDescription', 'Rivedi e modifica le tue risposte')} />
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <ClipLoader size={50} color="#3466b6" />
            <p className="mt-4 text-gray-600">{t('questionario.loading', 'Caricamento questionario...')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Le sezioni complete con le opzioni vengono ora prese direttamente da risposteData
  const sezioni = risposteData?.sezioni || {};
  const totaleRisposte = Object.values(sezioni).reduce((sum, s) => {
    return sum + (s.risposte ? s.risposte.length : 0);
  }, 0);
  const risposteCompilate = Object.values(sezioni).reduce((sum, s) => {
    if (!s.risposte || !Array.isArray(s.risposte)) return sum;
    
    return sum + s.risposte.filter(d => {
      if (!d || !d.domanda_id) return false;
      const answer = answers[d.domanda_id];
      return answer && (Array.isArray(answer) ? answer.length > 0 : (typeof answer === 'string' && answer.trim().length > 0));
    }).length;
  }, 0);
  const percentualeCompletamento = totaleRisposte > 0 
    ? Math.round((risposteCompilate / totaleRisposte) * 100) 
    : 0;

  const currentSezione = selectedSection ? sezioni[selectedSection] : null;

  // Conta le domande modificabili (NO/NON SO)
  const countEditableQuestions = () => {
    return Object.values(sezioni).reduce((count, sezione) => {
      if (!sezione.risposte) return count;
      return count + sezione.risposte.filter(d => isQuestionEditable(d)).length;
    }, 0);
  };
  const editableCount = countEditableQuestions();

  return (
    <div>
      <HeaderBanner title={t('Compliance.title')} linkTo='/compliance' description={t('questionario.reviewDescription', 'Rivedi e modifica le tue risposte')} />
      
      <div className="mx-auto max-w-7xl mt-5">
        {/* Info e filtro */}
        <div className="bg-white border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-black mb-1">
                {t('questionario.editableQuestionsTitle', 'Domande da migliorare')}
              </h3>
              <p className="text-sm text-black">
                {editableCount === 0 
                  ? t('questionario.noEditableQuestions', 'Ottimo! Non ci sono domande da migliorare.')
                  : t('questionario.editableQuestionsDesc', 'Hai {{count}} domande con risposta "NO" o "NON SO" che puoi migliorare.').replace('{{count}}', editableCount)
                }
              </p>
            </div>
            <button
              onClick={() => setFilterMode(prev => prev === 'editable' ? 'all' : 'editable')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center gap-2"
            >
              {filterMode === 'editable' 
                ? t('questionario.showAll', 'Mostra tutte') 
                : t('questionario.showEditable', 'Mostra solo modificabili')}
            </button>
          </div>
        </div>

        {/* Sezioni Tab */}
        <div className="flex space-x-4 mb-4">
          {Object.keys(sezioni).map((sectionName) => {
            const isActive = selectedSection === sectionName;
            const className = isActive 
              ? 'px-2 py-1 rounded-md bg-amber-300 text-black' 
              : 'px-2 py-1 rounded-md text-black border border-black cursor-pointer hover:bg-gray-100';

            return (
              <button
                key={sectionName}
                onClick={() => setSelectedSection(sectionName)}
                className={className}
              >
                {t(`nis2Categories.${sectionName}`, '') || sectionName}
              </button>
            );
          })}
        </div>

        {/* Domande della sezione */}
        {currentSezione && currentSezione.risposte && Array.isArray(currentSezione.risposte) ? (
          <div className="border p-4 mb-2 flex flex-col rounded-md">
            <h3 className="font-bold mb-2">{t(`nis2Categories.${selectedSection}`, '') || selectedSection}</h3>
            {filterQuestions(currentSezione.risposte).map((domanda, index) => {
              if (!domanda || !domanda.domanda_id) return null;
              
              const answer = answers[domanda.domanda_id];
              const isEditable = isQuestionEditable(domanda);
              const bgColor = !isEditable ? 'bg-gray-200' : (index % 2 !== 0 ? 'bg-gray-100' : 'bg-white');

              return (
                <div key={domanda.domanda_id} className={`flex items-center ${bgColor} ${!isEditable ? 'opacity-60' : ''}`}>
                  <div className="flex w-full p-3 items-center justify-between">
                    <div className='w-[60%] flex items-center gap-2'>
                      <p>{domanda.domanda_testo}</p>
                      {!isEditable && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {t('questionario.alreadyCompliant', 'Conforme')}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 items-center">
                      {(domanda.domanda_tipo === 'libera' || domanda.domanda_tipo === 'testo') ? (
                        <textarea 
                          className="border rounded w-full mt-2 p-2" 
                          value={answer || ''} 
                          onChange={(e) => handleTextChange(domanda.domanda_id, e.target.value)}
                          disabled={!isEditable}
                        />
                      ) : domanda.opzioni_disponibili && Array.isArray(domanda.opzioni_disponibili) && domanda.opzioni_disponibili.length > 0 ? (
                        domanda.opzioni_disponibili.map((opzione) => (
                          <div key={opzione.id} className="flex items-center">
                            <input
                              type={domanda.domanda_tipo === 'multipla' ? 'checkbox' : 'radio'}
                              name={domanda.domanda_id}
                              checked={Array.isArray(answer) && answer.includes(opzione.id) || false}
                              onChange={() => handleOptionChange(domanda.domanda_id, opzione.id, domanda.domanda_tipo === 'multipla')}
                              disabled={!isEditable}
                            />
                            <label className={`ml-2 ${!isEditable ? 'cursor-not-allowed' : 'cursor-pointer'}`}>{opzione.testo}</label>
                          </div>
                        ))
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            {filterMode === 'editable' && filterQuestions(currentSezione.risposte).length === 0 && (
              <div className="text-center py-8 text-green-600">
                <Icon name="check" className="text-4xl mb-2" />
                <p className="font-semibold">{t('questionario.allCompliant', 'Ottimo! Tutte le domande di questa sezione sono conformi.')}</p>
              </div>
            )}
            
            {/* Navigation buttons for each section */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <button
                onClick={goToPreviousSection}
                disabled={isFirstSection()}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span>←</span> {t('questionario.previousSection', 'Sezione Precedente')}
              </button>
              
              {!isLastSection() ? (
                <button
                  onClick={goToNextSection}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center gap-2"
                >
                  {t('questionario.nextSection', 'Avanti')} <span>→</span>
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? <ClipLoader size={20} color="#333333" /> : (
                    <>
                      <Icon name="check" />
                      {t('common.save', 'Salva Modifiche')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="border p-4 rounded-md text-center text-gray-500">
            {t('questionario.selectSection', 'Seleziona una sezione per visualizzare le domande')}
          </div>
        )}
      </div>
    </div>
  );
}
